/* global analytics */
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import useDispatch from 'hooks/useDispatch'

import { useDeferredCurrentVisit } from '../components/auth/withCurrentVisit'
import { getClientHistory } from '../appHistory'
import {
  deserializeSearchQuery,
  isValidSearchCountry,
} from '../actions/utils/search'
import { updateQueryString } from '../actions/searchActions'

type DetectedSearchLocation = {
  willDetect: boolean
  settled: boolean
  appliedSearch: string | null
}

type MountState = {
  pathname: string
  search: string
  willDetect: boolean
}

const useDetectedSearchLocation = (): DetectedSearchLocation => {
  const dispatch = useDispatch()
  const { pathname, search } = useLocation()

  const mount = useRef<MountState | null>(null)
  if (mount.current === null) {
    mount.current = {
      pathname,
      search,
      willDetect: !deserializeSearchQuery(search, pathname).country,
    }
  }
  const {
    pathname: mountPathname,
    search: mountSearch,
    willDetect,
  } = mount.current

  const { data, error, run } = useDeferredCurrentVisit()

  const started = useRef(false)
  useEffect(() => {
    if (!willDetect || started.current) return
    started.current = true
    run()
  }, [willDetect, run])

  const responded = data !== undefined || error !== undefined
  const [resolved, setResolved] = useState(false)
  const settled = !willDetect || resolved
  const appliedSearch = useRef<string | null>(null)
  const applied = useRef(false)

  useEffect(() => {
    if (!willDetect || applied.current || !responded) return
    applied.current = true
    // Must batch with the history change below: a consumer that unholds on
    // `settled` must not see the pre-detection url.
    setResolved(true)

    const visit = data?.data
    const country = visit?.country
    if (!country?.discoverable || !isValidSearchCountry(country.code)) return

    // The live location, not the render-time one: a filter change since mount
    // must win, and it lands here first.
    const history = getClientHistory()
    if (
      history.location.pathname !== mountPathname ||
      history.location.search !== mountSearch
    ) {
      return
    }

    const params = deserializeSearchQuery(mountSearch, mountPathname)
    const stateCode = visit?.state?.discoverable ? visit.state.code : undefined

    const url = dispatch(
      updateQueryString(
        {
          ...params,
          country: country.code,
          ...(stateCode ? { state: stateCode } : {}),
        },
        undefined,
        { replace: true, state: history.location.state },
      ),
    ) as unknown as string
    const queryIndex = url.indexOf('?')
    appliedSearch.current = queryIndex === -1 ? '' : url.slice(queryIndex)

    analytics.track('Search Market Detected', {
      market: visit?.market?.code ?? null,
      country: country.code,
      state: stateCode ?? null,
      searchType: params.type ?? null,
    })
  }, [willDetect, responded, data, dispatch, mountPathname, mountSearch])

  return { willDetect, settled, appliedSearch: appliedSearch.current }
}

export default useDetectedSearchLocation
